import axios from 'axios';
import useSWR from 'swr';
import { toast } from 'sonner';

import { formatAndThrowApiError } from '@/helpers/api';
import { localSwrConfig, fetchData } from '@/helpers/swr';

// The help centre is served from the docs bundle, not the database: the same
// articles for every company, so none of these keys carry a uid. A failed read
// throws with `.status`, and a 503 means the API could not load the bundle.

/** Every category in display order, each with its article summaries. */
const useHelpArticles = (shouldRevalidate = true) => {
  const swr = useSWR(`/help-article`, fetchData, shouldRevalidate ? undefined : localSwrConfig);

  return swr;
};

/**
 * Ranked matches for `search`, best first. A blank search fetches nothing.
 * The previous results stay up while the next query loads, so the dropdown does
 * not flash empty between keystrokes.
 */
const useHelpArticleSearch = (search) => {
  const url = search ? `/help-article?search=${encodeURIComponent(search)}` : null;
  const swr = useSWR(url, fetchData, { keepPreviousData: true });

  return swr;
};

/**
 * One article's summary and Markdown body. A slug may contain `/`, sent as %2F.
 * A 404 is an answer, not a failure, so it is not retried.
 */
const useHelpArticle = (slug) => {
  const swr = useSWR(`/help-article/${encodeURIComponent(slug)}`, fetchData, { shouldRetryOnError: false });

  return swr;
};

/**
 * Asks the help centre assistant. Takes the conversation so far, ending on the
 * user's question, and resolves to `{ answer, sources }`. Nothing is stored:
 * the API is stateless, so the caller sends the whole conversation each time.
 * A 503 means the assistant is off here, which the panel says itself, so it
 * raises no toast.
 */
const usePostHelpArticleAsk = () => {
  const postHelpArticleAsk = async (messages) => {
    try {
      const result = await axios.post(`/help-article/ask`, { messages });
      return result.data;
    } catch (error) {
      if (error.response?.status !== 503) {
        toast.error('An error occurred asking the help centre assistant');
      }
      formatAndThrowApiError(error);
    }
  };

  return postHelpArticleAsk;
};

export { useHelpArticles, useHelpArticleSearch, useHelpArticle, usePostHelpArticleAsk };
